import { call } from 'redux-saga/effects';
import { createReducer, isAnyOf } from '@reduxjs/toolkit';

import { PATH, PATH_NAME } from 'constants/Routes';

/** @returns {import('types/generic.types').GenericRequestAction} */
export const getRequestPayload =
  ({ method = 'GET', ...requestParams } = { method: 'GET' }) =>
    (url, meta = {}) => ({
      payload: {
        request: {
          ...requestParams,
          method,
          url,
        },
      },
      meta,
    });

/**
 * @template U
 * @param {U} payload
 * @param {object} [meta]
 */
export const getSuccessPayload = (payload, meta = {}) => ({
  payload,
  meta,
});

/**
 * @param {import('types/generic.types').GenericError} error
 * @param {object} [meta]
 */
export const getFailurePayload = (error, meta = {}) => ({
  payload: { error },
  error: true,
  meta,
});

/**
 * @param {import('types/generic.types').GenericError} error
 */
export function* putRequestErrors(error) {
  const is404 = error.isAxiosError && error.response?.status === 404;
  if (is404) {
    yield call(window.location.replace, PATH[PATH_NAME.PAGE_404].stringify());
  }
}

/**
 * A boolean UI reducer that flips to `true` on `onAction` and back to `false` on any
 * of `offActions` — the `isLoading`/`isPaying`/`isSent` pattern repeated across
 * `reducers/ui/**`. Each argument can be an action type string or an action creator.
 *
 * @param {string | import('@reduxjs/toolkit').ActionCreatorWithAnyPayload} onAction
 * @param {...(string | import('@reduxjs/toolkit').ActionCreatorWithAnyPayload)} offActions
 */
export const createToggleReducer = (onAction, ...offActions) => {
  const asMatcher = (action) => (typeof action === 'string' ? (a) => a.type === action : action);

  return createReducer(false, (builder) => {
    builder
      .addCase(onAction, () => true)
      .addMatcher(isAnyOf(...offActions.map(asMatcher)), () => false);
  });
};
